import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils/cn';

/**
 * WorkspaceButton — the compact button used inside workspace screens
 * (T-0141).
 *
 * Mirrors `.btn` + `.btn-sm` and the four variants in the prototype
 * tokens (`docs/requirement-library/prototype/tokens.css`): 30px high,
 * 12px medium label, a 14px leading icon. `primary` is the amber action,
 * `secondary` the bordered default, `tertiary` a text button, and
 * `destructive` the critical-outlined one. `md` is the 36px size used in
 * page headers and dialog footers. `asChild` styles a link (for example a
 * router `Link`) as the button.
 */

export type WorkspaceButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'destructive';

export interface WorkspaceButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: WorkspaceButtonVariant;
  size?: 'sm' | 'md';
  asChild?: boolean;
}

const VARIANT: Record<WorkspaceButtonVariant, string> = {
  primary:
    'border-accent-amber-600 bg-accent-amber-600 text-paper-0 hover:border-accent-amber-500 hover:bg-accent-amber-500',
  secondary: 'border-ink-200 bg-paper-0 text-ink-700 hover:border-ink-300 hover:bg-paper-50',
  tertiary:
    'border-transparent bg-transparent text-ink-700 hover:underline hover:underline-offset-[3px]',
  destructive:
    'border-posture-critical bg-paper-0 text-posture-critical hover:bg-posture-critical-tint',
};

export const WorkspaceButton = React.forwardRef<HTMLButtonElement, WorkspaceButtonProps>(
  (
    { variant = 'secondary', size = 'sm', type = 'button', asChild = false, className, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        {...(asChild ? {} : { type })}
        className={cn(
          'inline-flex flex-none items-center justify-center gap-1.5 whitespace-nowrap rounded-card border font-medium',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          size === 'sm' ? 'h-[30px] px-2.5 text-meta' : 'h-9 px-3.5 text-body-sm',
          VARIANT[variant],
          className,
        )}
        {...props}
      />
    );
  },
);
WorkspaceButton.displayName = 'WorkspaceButton';
